// common
import {
  getModule,
  VuexModule,
  Module,
  MutationAction,
  Action,
  Mutation,
} from "vuex-module-decorators";
import store from "@/store";

// interfaces
import { ICustomersState } from "@/interfaces/store/ICustomers";

// helpers
import _get from "lodash/get";
import {
  // parseConfigPosition,
  // parseCurrentViewFilters,
  parseViewsConfigs,
} from "@/app-search/helpers/app-search.helpers";
// import tableConfig from "@/app-search/tables/marketing-customers-config.json";
import { query } from "@/api/app-search/app-search";
import { postCustomer, patchCustomer } from "@/api/customer/customer";
import {
  EngineKeys,
  EngineTypes,
} from "@/app-search/services/request-app-search.service";
import _cloneDeep from "lodash/cloneDeep";
import viewConfig from "@/app-search/filters/marketing-customers-view-config.json";
import UserModule from "@/store/modules/user";
import { ICustomer } from "@/interfaces/responses/customer";
import NotificationService from "@/services/notification.service";
import BookingModule from "./booking";
import { LogLevel, LogService } from "@/services/logging";
import { handleErrorAndPrepareMessage } from "@/utils/helpers/common";
import { setCustomerInfo } from "@/utils/helpers/product-slot-booking";
import { cloneDeep, isEqual } from "lodash";
import { BookingStep } from "@/constants/booking";
import { buildPatchBookingPayload } from "@/utils/vue-helpers/booking";
import dayjs from "dayjs";
import { createCompositeId } from "@/utils/helpers/app";
import { saveUpdatedBooking } from "@/db/updated-bookings";

@Module({ dynamic: true, store, name: "Customers", namespaced: true })
class Customers extends VuexModule implements ICustomersState {
  public isLoading = true;
  public customers: any = {};
  public search = "";
  public filter: any = this.getCurrentViewFilters;
  public rows: any = [];
  public selectedCustomers: any = [];
  public editedCustomer: any = [];

  /**
   * Get customers data
   *
   * @returns {object[]}
   */
  public get getCustomersList(): any {
    return _get(this.customers, "dataModel", []);
  }

  /*
  /!**
   * Get user advanced settings
   *
   * @returns {any}
   *!/
  public get getAdvancedSettings(): any {
    return UserModule.getAdvancedSettings;
  }
*/

  /**
   * Get "otherData" of the selected group
   *
   * @returns {any}
   */
  public get getGroupOtherData(): any {
    return UserModule.getGroupOtherData;
  }

  /**
   * Get "marketing-customers" table config for view
   *
   * @returns {object}
   */
  public get getViewConfig(): any {
    const publicViewConfig = _get(
      this,
      "getGroupOtherData.tables['marketing-customers'].views",
      null
    );
    // const privateViewConfig = _get(this, "getAdvancedSettings.tables['marketing-customers'].views", null);

    const params = {
      defaultConfig: viewConfig,
      publicConfig: publicViewConfig,
      privateConfig: null,
      // privateConfig: privateViewConfig
    };

    return parseViewsConfigs(params);
  }

  /**
   * Get filters list from current (active) view
   *
   * @returns {object}
   */
  public get getCurrentViewFilters(): any {
    return _cloneDeep({
      query: "",
      options: {
        page: { size: 25, current: 1 },
        filters: {},
        // filters: parseCurrentViewFilters(this.getViewConfig, this.viewId)
        result_fields: {
          country: { raw: {} },
          email: { raw: {} },
          full_name: { raw: {} },
          first_name: { raw: {} },
          last_name: { raw: {} },
          id: { raw: {} },
          phone: { raw: {} },
          group_access: { raw: {} },
          city: { raw: {} },
          zip_code: { raw: {} },
          birth_date: { raw: {} },
          email_marketing_organiser: { raw: {} },
          title: { raw: {} },
          allergies: { raw: {} },
          address: { raw: {} },
          language: { raw: {} },
          company: { raw: {} },
        },
      },
    });
  }

  public get getSelectedCustomerId(): number | null {
    const customer = this.selectedCustomers.find(
      (c: any) => c.bookingUuid === BookingModule.selectedBookingUUID
    );
    if (customer) {
      return Number(customer.id);
    } else {
      return null;
    }
  }

  public get getSelectedCustomerEmail(): string {
    const customer = this.selectedCustomers.find(
      (c: any) => c.bookingUuid === BookingModule.selectedBookingUUID
    );
    if (customer) {
      return customer.email;
    } else {
      return "";
    }
  }

  public get getSelectedCustomerFirstName(): string {
    const customer = this.selectedCustomers.find(
      (c: any) => c.bookingUuid === BookingModule.selectedBookingUUID
    );
    if (customer) {
      const firstName = customer.fullName
        .split(" ")
        .find((_: any, index: any) => index === 0);
      return firstName;
    } else {
      return "";
    }
  }

  public get getSelectedCustomerAddress(): string {
    const customer = this.selectedCustomers.find(
      (c: any) => c.bookingUuid === BookingModule.selectedBookingUUID
    );
    if (customer) {
      const address = customer.address;
      return address;
    } else {
      return "";
    }
  }

  public get getSelectedCustomerLastName(): string {
    const customer = this.selectedCustomers.find(
      (c: any) => c.bookingUuid === BookingModule.selectedBookingUUID
    );
    if (customer) {
      const lastName = customer.fullName
        .split(" ")
        .find((_: any, index: any) => index === 1);
      return lastName;
    } else {
      return "";
    }
  }

  /**
   * Fetch customers list
   *
   * @param {object} payload: params for request
   * @returns {Promise<{ customers: object }>}
   */
  @MutationAction({ mutate: ["customers"] })
  public async fetchCustomers(payload: any): Promise<{ customers: any }> {
    const customers = await query(
      EngineTypes.CustomerList,
      EngineKeys.Customer,
      payload
    );

    return { customers };
  }

  /**
   * Look up a customer in the search index by email and return the matching
   * record. Used to source the country as an alpha-2 code: the booking object
   * only carries the country name, but the index stores the code the country select needs.
   *
   * @param {object} params: the id and email of the customer to match
   * @returns {Promise<any | null>} the matching indexed customer, or null
   */
  @Action({ rawError: true })
  public async fetchIndexedCustomer(params: {
    id: string | number;
    email: string;
  }): Promise<any | null> {
    const payload = {
      query: params.email,
      options: {
        page: { size: 10, current: 1 },
        filters: {},
        result_fields: {
          id: { raw: {} },
          country: { raw: {} },
        },
      },
    };

    const customers = await query(
      EngineTypes.CustomerList,
      EngineKeys.Customer,
      payload
    );
    const list = customers?.dataModel ?? [];

    return list.find((c: any) => String(c.id) === String(params.id)) ?? null;
  }

  /**
   * Create new customer
   *
   * @param {object} payload: params for request
   * @returns {Promise<ICustomer | boolean>}
   */
  @Action({ rawError: true })
  public async postCustomer(payload: any): Promise<ICustomer | boolean> {
    try {
      LogService.info(
        `Going to create attached new customer, booking: ${BookingModule.selectedBookingUUID}`,
        payload
      );
      const { data } = await postCustomer(payload);
      LogService.info(
        `Attached customer created, booking: ${BookingModule.selectedBookingUUID}`,
        data
      );
      return data.customers;
    } catch (error: any) {
      const errorDetails = handleErrorAndPrepareMessage(error);

      NotificationService.showCustomMessage(
        errorDetails.errorMessage,
        "warning"
      );
      LogService.info(
        `Error when POSTing new customer, booking: ${BookingModule.selectedBookingUUID}`,
        { params: { error: error, errorDetails: errorDetails } },
        LogLevel.Error
      );
      // throw error;
      return false;
    }
  }

  /**
   * Update customer
   *
   * @param {object} payload: params for request
   * @returns {Promise<ICustomer>}
   */
  @Action({ rawError: true })
  public async patchCustomer(payload: any): Promise<ICustomer> {
    return await patchCustomer(payload);
  }

  @Action
  public async setSelectedCustomers(selectedCustomers: any) {
    const alreadyExistingCustomer = this.selectedCustomers.find(
      (c: any) => c.bookingUuid === BookingModule.selectedBookingUUID
    );
    // one customer per booking: drop the current booking's customer before adding the new one
    if (alreadyExistingCustomer) {
      this.removeSelectedCustomer(alreadyExistingCustomer);
    }
    this.SET_SELECTED_CUSTOMERS(
      this.selectedCustomers.concat(selectedCustomers)
    );

    await this.syncSelectedCustomerToBooking();
  }

  /**
   * Remove the customer assigned to the current order (if any).
   */
  @Action
  public async unassignSelectedCustomer() {
    const assignedCustomer = this.selectedCustomers.find(
      (c: any) => c.bookingUuid === BookingModule.selectedBookingUUID
    );
    if (!assignedCustomer) {
      return;
    }
    this.removeSelectedCustomer(assignedCustomer);

    await this.syncSelectedCustomerToBooking();
  }

  /**
   * At the CompleteOrder step, push the current booking's customer (or its
   * removal) onto the booking object and persist the change with a PUT.
   */
  @Action
  public async syncSelectedCustomerToBooking() {
    if (
      BookingModule.selectedBooking &&
      BookingModule.bookingStep === BookingStep.CompleteOrder
    ) {
      const bookingCopy = cloneDeep(BookingModule.selectedBooking);
      setCustomerInfo(BookingModule.selectedBooking);
      const isSameObj = isEqual(bookingCopy, BookingModule.selectedBooking);
      if (!isSameObj) {
        const diffObject = buildPatchBookingPayload(
          bookingCopy,
          BookingModule.selectedBooking
        );
        try {
          await saveUpdatedBooking(diffObject);
        } catch (err: any) {
          LogService.info(
            `Error when saving the booking diff object to indexedDB, booking: ${bookingCopy.uuid}`
          );
        }
      }
    }
  }

  @Action
  public removeSelectedCustomer(customer: any) {
    this.removeCustomer(customer.id);
  }

  @Mutation
  private SET_SELECTED_CUSTOMERS(selectedCustomers: any) {
    this.selectedCustomers = selectedCustomers;
  }

  @Mutation
  private removeCustomer(id: string) {
    const index = this.selectedCustomers.findIndex((c: any) => c.id === id);
    if (index > -1) {
      this.selectedCustomers.splice(index, 1);
    }
  }

  @Action
  public setEditedCustomers(editedCustomer: any) {
    this.SET_EDITED_CUSTOMERS(editedCustomer);
  }

  @Mutation
  private SET_EDITED_CUSTOMERS(editedCustomer: any) {
    this.editedCustomer = editedCustomer;
  }

  @Mutation restore(moduleFields: Partial<ICustomersState>) {
    const { selectedCustomers } = moduleFields;

    if (selectedCustomers) {
      this.selectedCustomers = selectedCustomers;
    }
  }
}

const CustomersModule = getModule(Customers);
export default CustomersModule;
